import { 
  ArrowRight, 
  CheckCircle2, 
  ShieldAlert, 
  Camera, 
  Zap, 
  KeyRound, 
  Layers,
  FileText
} from 'lucide-react';
import { Button } from '../../../components/ui/Button.jsx';
import { SHOP_CATEGORIES } from '../../../lib/shop-taxonomy.js';

export function HighlightGridHero({
  tenant,
  catalogCount = null,
  onSearch,
  onSelectCategory,
  onNavigate,
  categories = SHOP_CATEGORIES,
  heroTitle,
  heroSubtitle,
  heroCtaPrimary,
  heroCtaSecondary,
}) {
  const tenantName = tenant?.name || 'ASL Partner-Shop';
  const heroBadge = tenant?.settings?.heroBadge || 'Fachgroßhandel';
  const heroHeadline = heroTitle || tenant?.settings?.heroHeadline || 'Professionelle Sicherheitstechnik.';
  const heroSubheadline = heroSubtitle || tenant?.settings?.heroSubheadline ||
    `Ihr Fachpartner für moderne Gefahrenmelde-, Video- und Zutrittssysteme. Entdecken Sie unser Fachhandels-Sortiment.`;

  const handleNav = (path) => {
    if (onNavigate) onNavigate(path);
  };

  const handleCategoryClick = (catSlug) => {
    if (onSelectCategory) onSelectCategory(catSlug);
    handleNav(`/catalog/${catSlug}`);
  };

  const showcaseCategories = (categories && categories.length >= 4)
    ? categories.slice(0, 4)
    : SHOP_CATEGORIES.slice(0, 4);

  const getCategoryIcon = (slug) => {
    switch (slug) {
      case 'alarmtechnik':
      case 'gefahrenmeldetechnik':
        return ShieldAlert;
      case 'videoueberwachung':
        return Camera;
      case 'stromversorgung-infrastruktur':
        return Zap;
      case 'zutrittskontrolle':
        return KeyRound;
      default:
        return Layers;
    }
  };

  return (
    <section
      data-hero-variant="highlight_grid"
      className="relative overflow-hidden rounded-3xl border border-[var(--border)] bg-white p-5 sm:p-10 lg:p-14 shadow-sm"
    >
      <div className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:items-center">
        
        {/* Left Side: 60% Headline & CTAs */}
        <div className="space-y-6 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-full bg-[var(--primary,#e3000b)]/10 px-3 py-1 text-xs font-bold uppercase tracking-wider text-[var(--primary,#e3000b)]">
              {heroBadge}
            </span>
            <span className="inline-flex items-center rounded-full border border-[var(--border)] bg-[var(--surface-container-low,#f8fafc)] px-3 py-1 text-xs font-medium text-[var(--secondary)]">
              {tenantName}
            </span>
          </div>

          <h1 className="font-display text-3xl font-extrabold tracking-tight text-[var(--foreground)] sm:text-5xl lg:text-6xl leading-[1.1] break-words">
            {heroHeadline} <br />
            <span className="text-[var(--primary,#e3000b)]">Für Gewerbe & Errichter.</span>
          </h1>

          <p className="text-sm sm:text-base leading-relaxed text-[var(--secondary)] max-w-xl">
            {heroSubheadline}
          </p>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center pt-2">
            <Button
              size="lg"
              className="h-12 px-6 text-sm sm:text-base font-bold shadow-sm cursor-pointer"
              onClick={() => handleNav('/catalog')}
            >
              {heroCtaPrimary || <span>Gesamten Katalog öffnen</span>}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="h-12 px-6 text-sm sm:text-base font-medium cursor-pointer"
              onClick={() => handleNav('/cart')}
            >
              <FileText className="mr-2 h-4 w-4 text-[var(--primary)]" />
              {heroCtaSecondary || <span>Projektangebot anfragen</span>}
            </Button>
          </div>

          <div className="pt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 border-t border-[var(--border)] text-xs font-semibold text-[var(--secondary)]">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[var(--primary)] shrink-0" />
              <span>Fachsortiment</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[var(--primary)] shrink-0" />
              <span>B2B Fachkonditionen</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[var(--primary)] shrink-0" />
              <span>Sicherheitstechnik</span>
            </div>
          </div>
        </div>

        {/* Right Side: 40% 2x2 Interactive Category Grid Showcase */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          {showcaseCategories.map((cat) => {
            const Icon = getCategoryIcon(cat.slug);
            const count = typeof cat.productCount === 'number'
              ? cat.productCount
              : (typeof cat.product_count === 'number' ? cat.product_count : null);
            const labelText = cat.shortLabel || cat.label || cat.name;

            return (
              <button
                key={cat.slug}
                type="button"
                onClick={() => handleCategoryClick(cat.slug)}
                className="group relative flex flex-col justify-between rounded-2xl border border-[var(--border)] bg-[var(--surface-container-low,#f8fafc)] p-4 sm:p-5 text-left transition-all hover:bg-white hover:border-[var(--primary)] hover:shadow-md cursor-pointer"
              >
                <div className="space-y-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-[var(--primary)] shadow-2xs group-hover:bg-[var(--primary)] group-hover:text-white transition-colors">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="font-display text-sm sm:text-base font-bold text-[var(--foreground)] group-hover:text-[var(--primary)] transition-colors leading-tight">
                      {labelText}
                    </h3>
                    <p className="text-xs text-[var(--secondary)] mt-1">
                      {typeof count === 'number' && count > 0 ? `${count} Artikel` : 'Systeme & Zubehör'}
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between text-xs font-bold text-[var(--primary)] opacity-0 group-hover:opacity-100 transition-opacity">
                  <span>Fachbereich &rarr;</span>
                </div>
              </button>
            );
          })}
        </div>

      </div>
    </section>
  );
}
